import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Upload
} from 'antd'
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import PageLoader from '../../components/Layouts/PageLoader'
import PrivateRoute from '../../hocs/PrivateRoute'
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  insuranceVehicleClassesUrl,
  insurancePolicyTypesUrl,
  insurersUrl,
  insurancePricingUrl,
  insuranceConfigUrl,
  insuranceBrokersUrl,
  insuranceSetupHealthUrl,
  insurerLogoUrl,
  successNotification,
  errorNotification
} from '../../config'

const { Option } = Select

const serverSaid = response =>
  response.error?.response?.data?.detail || response.data?.detail

const SetupPage = () => {
  const [loading, setLoading] = useState(false)
  const [classes, setClasses] = useState([])
  const [types, setTypes] = useState([])
  const [insurers, setInsurers] = useState([])
  const [pricing, setPricing] = useState([])
  const [brokers, setBrokers] = useState([])
  const [config, setConfig] = useState(null)
  const [health, setHealth] = useState(null)
  const [editing, setEditing] = useState(null) // {kind, row}
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  const load = async () => {
    setLoading(true)
    const fetches = [
      [`${insuranceVehicleClassesUrl}/`, setClasses],
      [`${insurancePolicyTypesUrl}/`, setTypes],
      [`${insurersUrl}/`, setInsurers],
      [`${insurancePricingUrl}/`, setPricing],
      [`${insuranceBrokersUrl}/`, setBrokers]
    ]
    for (const [url, setter] of fetches) {
      const response = await getRequest(url, 'setup', () => {})
      if (response.success) setter(response.data.results || response.data)
    }
    const cfg = await getRequest(`${insuranceConfigUrl}/`, 'config', () => {})
    if (cfg.success) {
      const rows = cfg.data.results || cfg.data
      setConfig(rows.find(r => r.country === 'GH') || rows[0] || null)
    }
    const hp = await getRequest(insuranceSetupHealthUrl, 'health', () => {})
    if (hp.success) setHealth(hp.data)
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line
  }, [])

  const save = async (url, payload, id) => {
    setSaving(true)
    const response = id
      ? await patchRequest(`${url}/${id}/`, payload, 'setup', () => {})
      : await postRequest(`${url}/`, payload, 'setup', () => {})
    setSaving(false)
    if (!response.success) {
      errorNotification(serverSaid(response) || 'Could not save that.')
      return false
    }
    successNotification('Saved.')
    setEditing(null)
    setForm({})
    await load()
    return true
  }

  const uploadLogo = async (id, file) => {
    setUploadingLogo(true)
    const data = new FormData()
    data.append('file', file)
    // The 5th argument is `defaultHeaders`; false sends multipart.
    const response = await postRequest(insurerLogoUrl(id), data, 'logo', () => {}, false)
    setUploadingLogo(false)
    if (!response.success) {
      errorNotification(serverSaid(response) || 'That image could not be uploaded.')
      return
    }
    successNotification('Logo updated.')
    setForm(current => ({ ...current, logoUrl: response.data.logoUrl }))
    await load()
  }

  const removeLogo = async id => {
    setUploadingLogo(true)
    const response = await deleteRequest(insurerLogoUrl(id), 'logo', () => {})
    setUploadingLogo(false)
    if (!response.success) {
      errorNotification(serverSaid(response) || 'That logo could not be removed.')
      return
    }
    setForm(current => ({ ...current, logoUrl: '' }))
    await load()
  }

  const openEditor = (kind, row) => {
    setEditing({ kind, row })
    setForm(row ? { ...row } : {})
  }

  /*
   * antd `render` functions return JSX, which the display-name rule reads as an anonymous
   * component. They are cell renderers, not components — disabled here the same way the
   * MSRP whitelist and car-request tables already do.
   */
  /* eslint-disable react/display-name */
  const classColumns = [
    {
      title: 'Position',
      dataIndex: 'position',
      width: 80,
      render: (_, r) => r.position ?? 0
    },
    { title: 'Class', dataIndex: 'name' },
    {
      title: 'Third-party premium',
      dataIndex: 'thirdPartyPremium',
      render: (_, r) => `GHS ${r.thirdPartyPremium}`
    },
    {
      title: 'Per extra seat',
      dataIndex: 'extraSeatAmount',
      render: (_, r) =>
        r.seatsExempt ? (
          <span style={{ opacity: 0.5 }}>No seat charge</span>
        ) : (
          `GHS ${r.extraSeatAmount || '0.00'}`
        )
    },
    {
      title: 'Comprehensive rate',
      dataIndex: 'comprehensiveRate',
      render: (_, r) => `${r.comprehensiveRate}% of the vehicle's value`
    },
    {
      title: 'Seats prefilled',
      dataIndex: 'defaultSeats',
      width: 120,
      // Blank is a real setting, not a gap: a minibus has no typical seat count, and an empty box
      // beats a wrong number the customer taps past. An exempt class is never asked at all.
      render: (_, r) =>
        r.seatsExempt ? (
          <Tag>Not asked</Tag>
        ) : r.defaultSeats ? (
          r.defaultSeats
        ) : (
          <span style={{ opacity: 0.5 }}>—</span>
        )
    },
    {
      title: 'Active',
      dataIndex: 'active',
      render: (_, r) => (r.active ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>)
    },
    {
      title: '',
      key: 'edit',
      render: (_, r) => (
        <Button size="small" onClick={() => openEditor('class', r)}>
          Edit
        </Button>
      )
    }
  ]

  const insurerColumns = [
    {
      title: '',
      key: 'logo',
      width: 56,
      render: (_, r) =>
        r.logoUrl ? (
          <Avatar shape="square" size={36} src={r.logoUrl} />
        ) : (
          // Initials rather than a blank cell: the app renders the same fallback, so this shows
          // what a customer would actually see on the comparison card.
          <Avatar shape="square" size={36}>
            {(r.name || '?')
              .split(' ')
              .map(word => word[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </Avatar>
        )
    },
    { title: 'Insurer', dataIndex: 'name' },
    {
      title: 'Writes',
      dataIndex: 'policyTypeNames',
      render: (_, r) => (r.policyTypeNames || []).join(', ') || '—'
    },
    {
      // The symptom is otherwise invisible: an unpriced combination is never quoted, and the
      // insurer simply does not appear on the customer's cards with nothing to explain why.
      title: 'Ready to quote',
      key: 'unpriced',
      render: (_, r) =>
        (r.unpricedCovers || []).length === 0 ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag color="red">No pricing for {r.unpricedCovers.join(', ')}</Tag>
        )
    },
    {
      title: 'Active',
      dataIndex: 'active',
      render: (_, r) => (r.active ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>)
    },
    {
      title: '',
      key: 'edit',
      render: (_, r) => (
        <Button size="small" onClick={() => openEditor('insurer', r)}>
          Edit
        </Button>
      )
    }
  ]

  const pricingColumns = [
    { title: 'Insurer', dataIndex: 'insurerName' },
    { title: 'Cover', dataIndex: 'policyTypeName' },
    {
      title: 'Our service fee',
      dataIndex: 'serviceFeeAmount',
      render: (_, r) => `GHS ${r.serviceFeeAmount}`
    },
    {
      title: 'Our commission',
      key: 'commission',
      render: (_, r) =>
        r.commissionKind === 'percent'
          ? `${r.commissionValue}% of the premium`
          : `GHS ${r.commissionValue}`
    },
    {
      title: '',
      key: 'edit',
      render: (_, r) => (
        <Button size="small" onClick={() => openEditor('pricing', r)}>
          Edit
        </Button>
      )
    }
  ]

  /* eslint-enable react/display-name */

  return (
    <PageLoader loading={loading}>
      {health && !health.ready && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This service is not fully configured"
          description={
            <ul style={{ marginBottom: 0 }}>
              {health.problems.map(problem => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          }
        />
      )}

      <Card
        size="small"
        title="Vehicle classes"
        style={{ marginBottom: 16 }}
        extra={
          <Button type="primary" size="small" onClick={() => openEditor('class', null)}>
            Add a class
          </Button>
        }
      >
        {/*
          The sentence that makes someone comfortable editing a live premium. It is true because
          every figure is snapshotted onto an application the moment the customer picks an insurer.
        */}
        <p style={{ opacity: 0.7 }}>
          Changing a rate affects new quotes only. Anyone who has already chosen an insurer keeps
          the price they were shown.
        </p>
        <Table rowKey="id" dataSource={classes} columns={classColumns} pagination={false} />
      </Card>

      <Card size="small" title="The seat rule and the limits" style={{ marginBottom: 16 }}>
        {config ? (
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <div style={{ opacity: 0.6, fontSize: 12 }}>Free seats</div>
              <InputNumber
                min={0}
                value={config.freeSeats}
                onChange={value => setConfig({ ...config, freeSeats: value })}
              />
              <p style={{ opacity: 0.65, fontSize: 11 }}>
                Seats below this threshold are free for all vehicle classes.
              </p>
            </Col>
            <Col xs={12} md={6}>
              <div style={{ opacity: 0.6, fontSize: 12 }}>Most seats allowed</div>
              <InputNumber
                min={1}
                value={config.maxSeats}
                onChange={value => setConfig({ ...config, maxSeats: value })}
              />
            </Col>
            <Col xs={12} md={6}>
              <div style={{ opacity: 0.6, fontSize: 12 }}>Most we will charge (GHS)</div>
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                value={config.maxChargeableAmount}
                onChange={value => setConfig({ ...config, maxChargeableAmount: value })}
              />
            </Col>
            <Col xs={24}>
              <p style={{ opacity: 0.7 }}>
                Per-seat charges are now set individually per vehicle class (see table above).
                Above the maximum chargeable amount, the app stops offering payment and tells
                the customer our team will arrange it — a guard against a mistyped rate becoming
                a real charge.
              </p>
              <Button
                type="primary"
                loading={saving}
                onClick={() =>
                  save(
                    insuranceConfigUrl,
                    {
                      freeSeats: config.freeSeats,
                      maxSeats: config.maxSeats,
                      maxChargeableAmount: config.maxChargeableAmount
                    },
                    config.id
                  )
                }
              >
                Save
              </Button>
            </Col>
          </Row>
        ) : (
          <p>No configuration row yet.</p>
        )}
      </Card>

      <Card
        size="small"
        title="Insurers"
        style={{ marginBottom: 16 }}
        extra={
          <Button type="primary" size="small" onClick={() => openEditor('insurer', null)}>
            Add an insurer
          </Button>
        }
      >
        <Table rowKey="id" dataSource={insurers} columns={insurerColumns} pagination={false} />
      </Card>

      <Card
        size="small"
        title="What we make"
        extra={
          <Button type="primary" size="small" onClick={() => openEditor('pricing', null)}>
            Add pricing
          </Button>
        }
      >
        <p style={{ opacity: 0.7 }}>
          One row per insurer and cover. An insurer with no row here is never quoted at all —
          a policy sold with no service fee and no commission is a policy sold at a loss.
        </p>
        <Table rowKey="id" dataSource={pricing} columns={pricingColumns} pagination={false} />
      </Card>

      <Modal
        visible={editing?.kind === 'class'}
        title={editing?.row ? 'Edit the class' : 'Add a vehicle class'}
        confirmLoading={saving}
        onCancel={() => setEditing(null)}
        onOk={() =>
          save(
            insuranceVehicleClassesUrl,
            {
              country: form.country || 'GH',
              code: form.code,
              name: form.name,
              description: form.description || '',
              thirdPartyPremium: form.thirdPartyPremium,
              extraSeatAmount: form.extraSeatAmount || 0,
              comprehensiveRate: form.comprehensiveRate,
              seatsExempt: form.seatsExempt === true,
              // null, not 0: "no typical number" is not "nought seats".
              defaultSeats: form.defaultSeats ?? null,
              position: form.position ?? 0,
              active: form.active !== false
            },
            editing?.row?.id
          )
        }
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <label>Name</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </Col>
          <Col xs={24} md={12}>
            <label>Code</label>
            <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} />
          </Col>
          <Col xs={24}>
            <label>Description</label>
            <Input
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="What this class covers (e.g., Standard passenger cars for personal use)"
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Third-party premium (GHS)</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={form.thirdPartyPremium}
              onChange={value => setForm({ ...form, thirdPartyPremium: value })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Per extra seat (GHS)</label>
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: '100%' }}
              disabled={form.seatsExempt === true}
              value={form.extraSeatAmount}
              onChange={value => setForm({ ...form, extraSeatAmount: value })}
            />
            <p style={{ opacity: 0.65, fontSize: 12 }}>
              Charged for each seat above the free limit (currently 5 seats).
            </p>
          </Col>
          <Col xs={24} md={12}>
            <label style={{ display: 'block' }}>No seat charge</label>
            <Switch
              checked={form.seatsExempt === true}
              onChange={value => setForm({ ...form, seatsExempt: value })}
            />
            <p style={{ opacity: 0.65, fontSize: 12 }}>
              On: this class pays nothing per seat, so the flow never asks how many it has and the
              seat part of the price is zero. Off is the normal case and what every class did
              before this switch existed.
            </p>
          </Col>
          <Col xs={24} md={12}>
            <label>Comprehensive rate (% of value)</label>
            <InputNumber
              min={0}
              step={0.1}
              style={{ width: '100%' }}
              value={form.comprehensiveRate}
              onChange={value => setForm({ ...form, comprehensiveRate: value })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Seats prefilled in the quote</label>
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              disabled={form.seatsExempt === true}
              value={form.defaultSeats ?? null}
              onChange={value => setForm({ ...form, defaultSeats: value })}
            />
            <p style={{ opacity: 0.65, fontSize: 12 }}>
              What the seat box opens on for this class — a suggestion the customer can change,
              never the answer. Leave it empty where no number is typical (a minibus), so the box
              opens blank instead of nudging somebody past a wrong figure.
            </p>
          </Col>
          <Col xs={24} md={12}>
            <label>Display position</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={form.position ?? 0}
              onChange={value => setForm({ ...form, position: value })}
            />
            <p style={{ opacity: 0.65, fontSize: 12 }}>
              Lower numbers appear first. Use 0, 10, 20... to leave room for future insertions.
            </p>
          </Col>
          <Col xs={24}>
            <label style={{ display: 'block' }}>Active</label>
            <Switch
              checked={form.active !== false}
              onChange={value => setForm({ ...form, active: value })}
            />
          </Col>
        </Row>
      </Modal>

      <Modal
        visible={editing?.kind === 'insurer'}
        title={editing?.row ? 'Edit the insurer' : 'Add an insurer'}
        confirmLoading={saving}
        onCancel={() => setEditing(null)}
        onOk={() =>
          save(
            insurersUrl,
            {
              name: form.name,
              shortName: form.shortName || '',
              broker: form.broker || null,
              policyTypes: form.policyTypes || [],
              active: form.active !== false,
              position: form.position || 0
            },
            editing?.row?.id
          )
        }
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <label>Name</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </Col>
          <Col xs={24} md={12}>
            <label style={{ display: 'block' }}>Logo</label>
            {/*
              Uploaded, not linked. A URL pointing at the insurer's own site breaks the day they
              redesign it, and the place it breaks is the comparison card where a customer decides
              who to trust with a year of cover.

              Only offered on an insurer that already exists, because the upload is addressed by
              id — save the insurer first, then give it a logo.
            */}
            {editing?.row ? (
              <Space>
                {form.logoUrl ? (
                  <Avatar shape="square" size={48} src={form.logoUrl} />
                ) : null}
                <Upload
                  accept="image/png,image/jpeg,image/webp"
                  showUploadList={false}
                  beforeUpload={file => {
                    void uploadLogo(editing.row.id, file)
                    return false
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={uploadingLogo}>
                    {form.logoUrl ? 'Replace' : 'Upload'}
                  </Button>
                </Upload>
                {form.logoUrl ? (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => void removeLogo(editing.row.id)}
                  />
                ) : null}
              </Space>
            ) : (
              <p style={{ opacity: 0.65 }}>Save the insurer first, then add its logo.</p>
            )}
            <p style={{ opacity: 0.65, fontSize: 12 }}>
              PNG, JPG or WebP, under 1MB. It appears on the customer&apos;s comparison card.
            </p>
          </Col>
          <Col xs={24} md={12}>
            <label>Broker</label>
            <Select
              allowClear
              style={{ width: '100%' }}
              value={form.broker}
              onChange={value => setForm({ ...form, broker: value })}
            >
              {brokers.map(b => (
                <Option key={b.id} value={b.id}>
                  {b.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={12}>
            <label>Covers it writes</label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              value={form.policyTypes}
              onChange={value => setForm({ ...form, policyTypes: value })}
            >
              {types.map(t => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24}>
            <label style={{ display: 'block' }}>Active</label>
            <Switch
              checked={form.active !== false}
              onChange={value => setForm({ ...form, active: value })}
            />
          </Col>
        </Row>
      </Modal>

      <Modal
        visible={editing?.kind === 'pricing'}
        title={editing?.row ? 'Edit what we make' : 'Add pricing'}
        confirmLoading={saving}
        onCancel={() => setEditing(null)}
        onOk={() =>
          save(
            insurancePricingUrl,
            {
              country: form.country || 'GH',
              insurer: form.insurer,
              policyType: form.policyType,
              serviceFeeAmount: form.serviceFeeAmount,
              commissionKind: form.commissionKind || 'percent',
              commissionValue: form.commissionValue,
              active: form.active !== false
            },
            editing?.row?.id
          )
        }
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <label>Insurer</label>
            <Select
              style={{ width: '100%' }}
              value={form.insurer}
              onChange={value => setForm({ ...form, insurer: value })}
            >
              {insurers.map(i => (
                <Option key={i.id} value={i.id}>
                  {i.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={12}>
            <label>Cover</label>
            <Select
              style={{ width: '100%' }}
              value={form.policyType}
              onChange={value => setForm({ ...form, policyType: value })}
            >
              {types.map(t => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={12}>
            <label>Our service fee (GHS)</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={form.serviceFeeAmount}
              onChange={value => setForm({ ...form, serviceFeeAmount: value })}
            />
            <p style={{ opacity: 0.65, fontSize: 12 }}>
              Added to the premium. The customer sees one total, never this figure.
            </p>
          </Col>
          <Col xs={24} md={12}>
            <label>Our commission</label>
            <Space>
              <Select
                style={{ width: 120 }}
                value={form.commissionKind || 'percent'}
                onChange={value => setForm({ ...form, commissionKind: value })}
              >
                <Option value="percent">Percent</Option>
                <Option value="fixed">Fixed</Option>
              </Select>
              <InputNumber
                min={0}
                step={0.1}
                value={form.commissionValue}
                onChange={value => setForm({ ...form, commissionValue: value })}
              />
            </Space>
            <p style={{ opacity: 0.65, fontSize: 12 }}>
              What the broker owes us, once the policy is underwritten.
            </p>
          </Col>
        </Row>
      </Modal>
    </PageLoader>
  )
}

const SetupRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={SetupPage} />
  </RouterSwitch>
)

SetupRoutes.propTypes = { match: PropTypes.object }

export default withRouter(SetupRoutes)
